/**
 * @zendir/ui - Design Tokens (core scalars)
 *
 * Exportable design tokens for use in CSS and JavaScript.
 * Based on Astro UX Design System.
 *
 * These are the shared scalar primitives (spacing, radius, typography,
 * shadows, animation) plus the base color reference table. Status colors
 * are sourced from the Astro UXDS ramps in tokens/palettes so there is a
 * single source of truth for severity values across the whole system.
 */

import {
  astroStatus,
  astroStatusLightFill,
  astroStatusLightBorder,
} from "./palettes/astro";

export const colors = {
  // Status colors — sourced from the Astro UXDS ramps in tokens/palettes.
  status: { ...astroStatus },
  statusLightFill: { ...astroStatusLightFill },
  statusLightBorder: { ...astroStatusLightBorder },

  // Background Colors
  background: {
    dark: {
      base: "#101923",
      surface: "#1b2d3e",
      elevated: "#243647",
      overlay: "rgba(0, 0, 0, 0.7)",
    },
    light: {
      base: "#f5f6f9",
      surface: "#ffffff",
      elevated: "#ffffff",
      overlay: "rgba(0, 0, 0, 0.5)",
    },
  },

  // Text Colors
  text: {
    dark: {
      primary: "#ffffff",
      secondary: "#9ea7ad",
      tertiary: "#6b7175",
      muted: "#52595f",
    },
    light: {
      primary: "#1b2d3e",
      secondary: "#4c5a67",
      tertiary: "#7a8891",
      muted: "#a4adb5",
    },
  },

  // Accent Colors
  accent: {
    primary: "#4dacff",
    secondary: "#3d8ddb",
    tertiary: "#2e6eb3",
  },

  // Border Colors
  border: {
    dark: {
      default: "#3c4b5a",
      muted: "#2b3d4e",
      focus: "#4dacff",
    },
    light: {
      default: "#c4c8cc",
      muted: "#e1e4e8",
      focus: "#4dacff",
    },
  },
} as const;

export const spacing = {
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  xxl: "48px",
} as const;

export const borderRadius = {
  none: "0",
  sm: "2px",
  md: "4px",
  lg: "8px",
  xl: "16px",
  full: "9999px",
} as const;

/**
 * Typography tokens following AstroUXDS Design System
 * @see https://www.astrouxds.com/foundations/typography/
 *
 * Font weights per Astro spec: 300 (Light), 400 (Regular), 500 (Medium), 700 (Bold)
 * Note: Astro does NOT use 600 (semibold) - use 500 (medium) or 700 (bold) instead
 */
export const typography = {
  fontFamily: {
    /** Heading font: Sora (Zendir Brand Standard) */
    heading:
      '"Sora", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    /** Primary font: Public Sans (Zendir Brand Standard) */
    primary:
      '"Public Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    /** Monospace font: Roboto Mono for data/code */
    mono: '"Roboto Mono", "SF Mono", "Consolas", "Liberation Mono", monospace',
  },
  fontSize: {
    // Compact sizes for dense UIs (cards, badges, data labels)
    micro: "0.5625rem", // 9px - very tight spaces
    xxs: "0.625rem", // 10px - compact labels
    // Standard sizes
    xs: "0.75rem", // 12px - small text, body3
    sm: "0.875rem", // 14px - body2
    base: "1rem", // 16px - body1
    md: "1.125rem", // 18px - h5/h6
    lg: "1.25rem", // 20px - h3/h4
    xl: "1.5rem", // 24px - h2
    xxl: "2.125rem", // 34px - h1
    xxxl: "3rem", // 48px - display2
    display: "3.75rem", // 60px - display1
  },
  /**
   * Font weights per AstroUXDS specification
   * Only 300, 400, 500, 700 are officially supported
   */
  fontWeight: {
    light: 300,
    normal: 400,
    regular: 400, // Alias for normal
    medium: 500,
    bold: 700,
    // DEPRECATED: semibold is not in Astro spec, use medium (500) instead
    /** @deprecated Use medium (500) instead - Astro spec only supports 300, 400, 500, 700 */
    semibold: 500,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

export const shadows = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.3)",
  md: "0 2px 8px rgba(0, 0, 0, 0.3)",
  lg: "0 4px 16px rgba(0, 0, 0, 0.3)",
  xl: "0 8px 32px rgba(0, 0, 0, 0.4)",
  glow: "0 0 20px rgba(77, 172, 255, 0.4)",
} as const;

export const animation = {
  fast: "150ms ease",
  normal: "250ms ease",
  slow: "400ms ease",
} as const;
